import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import { RedirectError } from 'src/shared/error';

import { differenceInDays } from 'src/app/shared/utils/dates';

import type { GammaPetitionDetailsDataQuery, GammaPetitionDetailsDataQueryVariables } from './api.graphql';
import { GammaPetitionDetailsData } from './api.graphql';
import { getPetitionAdvocates } from './getPetitionAdvocates';
import type { AdvocatesForPetitionQuery } from './getPetitionAdvocates/petitionAdvocates.graphql.d.ts';
import { getPetitionStrengthScore } from './getPetitionStrengthScore';
import { getSupporterVoicesVideos } from './supporterVoicesVideos';
import type { PetitionDetailsPageData } from './types';

async function fetchPetitionDetails(utilityContext: UtilityContext, variables: GammaPetitionDetailsDataQueryVariables) {
	const {
		gql: { fetch },
	} = utilityContext;
	const { data } = await fetch<GammaPetitionDetailsDataQuery, GammaPetitionDetailsDataQueryVariables>({
		query: GammaPetitionDetailsData,
		variables,
		rejectOnError: true,
		// to separate query from other batched queries
		batched: false,
	});

	return data;
}

function normalizePetition(
	petition: NonNullable<GammaPetitionDetailsDataQuery['petition']>,
): PetitionDetailsPageData['petition'] {
	return {
		...petition,
		dmsWithLegislativeBodiesConnection: {
			pageInfo: { hasNextPage: petition.dmsWithLegislativeBodiesConnection.pageInfo.hasNextPage },
			totalCount: petition.dmsWithLegislativeBodiesConnection.totalCount,
			nodes: petition.dmsWithLegislativeBodiesConnection.nodes,
		},
	};
}

export async function getPetitionDetailsPageData(
	utilityContext: UtilityContext,
	slugOrId: string,
	{
		locale,
		countryCode,
		loadBandits,
	}: {
		locale: string;
		countryCode: string;
		loadBandits: boolean;
		hasShowLegislativeBodiesParam: boolean;
	},
): Promise<PetitionDetailsPageData> {
	const data = await fetchPetitionDetails(utilityContext, {
		slugOrId,
		locale,
		countryCode,
		loadBandits,
		commentsSortBy: 'SCORE',
	});

	if (!data.petition) throw new RedirectError({ url: '/?petition_not_found=true' });

	const supporterVoicesVideos = await getSupporterVoicesVideos(
		utilityContext,
		slugOrId,
		!!data.petition.videoSettingsMetadata.disableDisplay,
	);

	const petition = normalizePetition(data.petition);

	let petitionAdvocates: AdvocatesForPetitionQuery['getAdvocatesForPetition'] = [];

	if (petition.isCommunityPetition) {
		const { getAdvocatesForPetition } = await getPetitionAdvocates(utilityContext, {
			slugOrId: petition.id,
		});

		petitionAdvocates = getAdvocatesForPetition;
	}

	return {
		petition,
		recommendedDecisionMakers: data.recommendedDecisionMakers?.items || [],
		createdLessThanOneDayAgo: differenceInDays(new Date(), new Date(data.petition.createdAt)) < 1,
		score: getPetitionStrengthScore(data.petition),
		supporterVoicesVideos,
		mediaHitsPage: {
			isLastPage: !petition.mediaHitsConnection.pageInfo.hasNextPage,
			numberOfStarterMedia: petition.mediaHitsConnection.totalCount,
			mediaHits: petition.mediaHitsConnection.nodes,
		},
		featureConfigs: data.featureConfigs,
		petitionAdvocates,
	};
}
